/**
 * Transform distribusi umur.
 *
 * Data source (`/api/kependudukan/distribusiumur/find-many`) mengembalikan baris
 * mentah **lintas tahun** tanpa agregasi dan tanpa urutan. Satu respons bisa
 * memuat `65+` tahun 2021 dan `65+` tahun 2026 sekaligus; tanpa filter keduanya
 * tampil sebagai dua bar terpisah dan terbaca seolah satu tahun.
 *
 * Helper ini memfilter per tahun, menjumlahkan rentang yang sama, dan mengurutkan
 * dari batas bawah terkecil — mengikuti pola yang sudah dipakai `./religion.ts`
 * supaya kartu umur dan kartu agama berperilaku sama. Dipakai bersama oleh kartu
 * dashboard dan builder video wall agar keduanya tidak menyimpang.
 */

export interface RawAgeRow {
	rentangUmur?: unknown;
	range?: unknown;
	ageRange?: unknown;
	kelompokUmur?: unknown;
	jumlah?: unknown;
	total?: unknown;
	count?: unknown;
	tahun?: unknown;
	year?: unknown;
}

export interface AgeBucket {
	ageRange: string;
	total: number;
	/** Tahun baris ini; `null` bila sumber tidak mengirimkannya. */
	tahun: number | null;
}

/** Rentang tak dikenal diletakkan paling akhir, bukan dibuang. */
const UNKNOWN_ORDER = Number.MAX_SAFE_INTEGER;

function asArray<T>(raw: unknown): T[] {
	return Array.isArray(raw) ? (raw as T[]) : [];
}

function firstDefined(...vals: unknown[]): unknown {
	return vals.find((v) => v !== undefined && v !== null);
}

function toNumber(v: unknown): number {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
}

/** Tahun sebuah baris (`null` bila tidak ada / tidak valid). */
export function rowAgeYear(row: RawAgeRow): number | null {
	const raw = firstDefined(row.tahun, row.year);
	if (raw === undefined) return null;
	const n = Number(raw);
	return Number.isFinite(n) ? n : null;
}

/**
 * Kunci urutan sebuah rentang = batas bawahnya. `"6-12"` → 6, `"65+"` → 65.
 * Label tanpa angka (mis. `"Unknown"`) jatuh ke akhir supaya tetap terlihat.
 */
export function ageRangeOrder(ageRange: string): number {
	const match = ageRange.match(/\d+/);
	return match ? Number(match[0]) : UNKNOWN_ORDER;
}

/** Baris mentah API → bentuk seragam, tanpa filter apa pun. */
export function normalizeAgeRows(raw: unknown): AgeBucket[] {
	return asArray<RawAgeRow>(raw).map((row) => ({
		ageRange: String(
			firstDefined(
				row.rentangUmur,
				row.range,
				row.ageRange,
				row.kelompokUmur,
			) ?? "Unknown",
		),
		total: toNumber(firstDefined(row.jumlah, row.total, row.count)),
		tahun: rowAgeYear(row),
	}));
}

/** Daftar tahun unik yang tersedia, urut menurun (terbaru dulu). */
export function ageYears(rows: readonly AgeBucket[]): number[] {
	const years = new Set<number>();
	for (const row of rows) {
		if (row.tahun !== null) years.add(row.tahun);
	}
	return [...years].sort((a, b) => b - a);
}

/**
 * Filter per tahun + gabungkan rentang kembar + urutkan dari batas bawah terkecil.
 *
 * @param tahun bila diberikan, hanya baris tahun tsb yang diikutkan; `null`/undefined
 *   = ikutkan semua tahun (dijumlahkan per rentang).
 */
export function groupAge(
	rows: readonly AgeBucket[],
	tahun?: number | null,
): AgeBucket[] {
	const byRange = new Map<string, AgeBucket>();

	for (const row of rows) {
		if (tahun != null && row.tahun !== tahun) continue;
		const existing = byRange.get(row.ageRange);
		if (existing) existing.total += row.total;
		else byRange.set(row.ageRange, { ...row, tahun: tahun ?? row.tahun });
	}

	return [...byRange.values()].sort(
		(a, b) => ageRangeOrder(a.ageRange) - ageRangeOrder(b.ageRange),
	);
}

/**
 * Jalan pintas yang dipakai konsumen tanpa pemilih tahun (mis. video wall):
 * ambil tahun terbaru yang tersedia, lalu group.
 */
export function groupAgeLatestYear(rows: readonly AgeBucket[]): AgeBucket[] {
	return groupAge(rows, ageYears(rows)[0] ?? null);
}
